import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PAGE_SIZES,
  clearFilters as clearQueryFilters,
  mergeListParams,
  parseListQuery,
  serializeListQuery,
  withFilter,
  withLimit,
  withPage,
  withSearch,
  withSort,
  type ListQuery,
  type ListQueryConfig,
} from '@/lib/listQuery';

export interface UseListQueryOptions {
  /** localStorage key remembering the chosen page size across visits (seeds the default when the URL has no `limit`). */
  limitStorageKey?: string;
}

export interface ListQueryControls {
  query: ListQuery;
  setSearch: (search: string) => void;
  setFilter: (key: string, value: string | undefined) => void;
  setSort: (column: string) => void;
  setPage: (page: number, total: number) => void;
  setLimit: (limit: number) => void;
  clearFilters: () => void;
}

function readStoredLimit(key: string | undefined): number | undefined {
  if (!key) return undefined;
  try {
    const n = Number(localStorage.getItem(key));
    return (PAGE_SIZES as readonly number[]).includes(n) ? n : undefined;
  } catch {
    return undefined;
  }
}

/**
 * URL-driven list state. `config` MUST have a stable identity (declare it as a module-level constant):
 * it is a dependency of the memoised query and of every setter.
 *
 * Every write uses `replace` (typing/filtering never adds history entries) and keeps unrelated
 * params. react-router's functional `setSearchParams` does NOT queue (its updater sees the last
 * rendered snapshot), so two setters in one event would clobber each other. Instead a ref holds the
 * latest params: each write builds on it and updates it immediately, so a second setter in the same
 * tick builds on the first. The ref is only written in handlers and a layout effect, never during render.
 * The stored page size seeds the default limit and tracks the user's last `setLimit` choice.
 * Storage failures are ignored.
 */
export function useListQuery(config: ListQueryConfig, opts: UseListQueryOptions = {}): ListQueryControls {
  const { limitStorageKey } = opts;
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedLimit, setStoredLimit] = useState(() => readStoredLimit(limitStorageKey));
  const latest = useRef(searchParams);
  // Layout effect (ref-only, no setState): child passive effects (e.g. ResourceList's page clamp) run
  // before a parent's passive effects, so a passive sync here could let them merge stale params.
  useLayoutEffect(() => {
    latest.current = searchParams;
  }, [searchParams]);

  const effective = useMemo<ListQueryConfig>(
    () => (storedLimit === undefined ? config : { ...config, defaultLimit: storedLimit }),
    [config, storedLimit],
  );
  const query = useMemo(() => parseListQuery(searchParams, effective), [searchParams, effective]);

  const update = useCallback(
    (change: (q: ListQuery) => ListQuery) => {
      const prev = latest.current;
      const current = parseListQuery(prev, effective);
      const next = change(current);
      if (next === current) return;
      const merged = mergeListParams(prev, serializeListQuery(next, effective), effective);
      latest.current = merged;
      setSearchParams(merged, { replace: true });
    },
    [effective, setSearchParams],
  );

  const setSearch = useCallback((s: string) => update((q) => withSearch(q, s)), [update]);
  const setFilter = useCallback((k: string, v: string | undefined) => update((q) => withFilter(q, k, v)), [update]);
  const setSort = useCallback((col: string) => update((q) => withSort(q, col, effective)), [update, effective]);
  const setPage = useCallback((p: number, total: number) => update((q) => withPage(q, p, total)), [update]);
  const clearFilters = useCallback(() => update(clearQueryFilters), [update]);
  const setLimit = useCallback(
    (n: number) => {
      if ((PAGE_SIZES as readonly number[]).includes(n)) {
        setStoredLimit(n);
        if (limitStorageKey) {
          try {
            localStorage.setItem(limitStorageKey, String(n));
          } catch {
            /* storage unavailable: the URL still carries the limit */
          }
        }
      }
      update((q) => withLimit(q, n));
    },
    [update, limitStorageKey],
  );

  return { query, setSearch, setFilter, setSort, setPage, setLimit, clearFilters };
}
